import { useEffect, useRef } from "react";
import type { CatalogItem } from "@apex/shared";
import type { ViewType, WidgetAction, WidgetState } from "../state";

/**
 * Subset of widget state we persist across refresh.
 *
 * Notably absent: `quoteForm` (PII never goes anywhere outside the tab) and
 * loaded catalog data (we re-fetch on hydration so prices stay fresh).
 */
export interface PersistedState {
  /** Selected root model code (NOT id — codes are stable across deploys). */
  modelCode: string | null;
  /** Selected accessory codes. */
  itemCodes: string[];
  /** Current view (where to land after hydration). */
  view: ViewType;
  /** Active filter key → value map. */
  activeFilters: Record<string, string>;
}

/** Subset encoded in the URL hash — view, model, items only (no filters, no PII). */
export interface UrlHashState {
  modelCode: string;
  itemCodes: string[];
  view: ViewType;
}

/**
 * Build the per-tenant per-key sessionStorage key. We strip the API key down
 * to a short prefix so the key is reasonable in length and doesn't leak the
 * full secret to anyone who pokes around in DevTools.
 */
export function buildStorageKey(tenantSlug: string, apiKey: string): string {
  // Strip the "apex_widget_" or similar prefix and keep first 6 chars of the
  // suffix. This gives 6 chars of entropy — enough to disambiguate widgets
  // on the same tenant page, none of the secret material.
  const cleaned = apiKey.replace(/^apex_widget_?/, "");
  const prefix = cleaned.slice(0, 6);
  return `apex-widget:${tenantSlug}:apex_widget_${prefix}`;
}

/**
 * Serialise a UrlHashState into a `#m=...&i=...&v=...` style URL hash.
 */
export function serializeUrlHash(state: UrlHashState): string {
  const parts: string[] = [];
  parts.push(`m=${encodeURIComponent(state.modelCode)}`);
  if (state.itemCodes.length > 0) {
    parts.push(`i=${state.itemCodes.map(encodeURIComponent).join(",")}`);
  }
  parts.push(`v=${encodeURIComponent(state.view)}`);
  return `#${parts.join("&")}`;
}

/**
 * Parse a URL hash into a structured value, or null if the hash is empty
 * or doesn't carry a model code.
 */
export function parseUrlHash(hash: string): UrlHashState | null {
  if (!hash || hash === "#") return null;
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!stripped) return null;

  const params = new URLSearchParams(stripped);
  const modelCode = params.get("m");
  if (!modelCode) return null;

  const itemsRaw = params.get("i") ?? "";
  const itemCodes = itemsRaw === "" ? [] : itemsRaw.split(",").filter(Boolean);
  const viewRaw = (params.get("v") ?? "selection") as ViewType;

  return { modelCode, itemCodes, view: viewRaw };
}

/** Serialise the persisted state for sessionStorage. */
export function serializePersistedState(state: PersistedState): string {
  return JSON.stringify(state);
}

/** Parse a serialised persisted state. Returns null if the input is unusable. */
export function parsePersistedState(raw: string | null | undefined): PersistedState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed == null) return null;
    return parsed as PersistedState;
  } catch {
    return null;
  }
}

export interface UseStatePersistenceOptions {
  /** Tenant slug, e.g. "ascentra". Used to namespace sessionStorage. */
  tenantSlug: string;
  /** API key used for this widget instance. */
  apiKey: string;
  /** Current widget state. */
  state: WidgetState;
  /** Reducer dispatch. */
  dispatch: React.Dispatch<WidgetAction>;
  /**
   * Load a root model the same way a user click does: dispatch
   * SELECT_ROOT_ITEM AND fetch its compatible items, ending in
   * ROOT_ITEM_LOADED. Hydration MUST restore the persisted model through this
   * rather than a bare `dispatch({ type: "SELECT_ROOT_ITEM" })`, which flips
   * `isLoading` on without ever fetching — leaving the widget stranded on the
   * loading skeleton after a refresh (no compatible-items request is made, so
   * ROOT_ITEM_LOADED never fires and isLoading never clears).
   */
  selectRootItem: (model: CatalogItem) => void | Promise<void>;
  /**
   * Resolve a model code to the corresponding CatalogItem, or null if the
   * code is no longer valid. Used during hydration to validate persisted
   * model selections against the freshly-fetched catalog.
   */
  resolveModelByCode?: (code: string) => CatalogItem | null;
  /**
   * Filter persisted item codes against the freshly-loaded items list,
   * returning the IDs of those still present.
   */
  resolveItemIdsByCodes?: (codes: string[]) => string[];
}

/**
 * useStatePersistence — keeps widget state in sync with sessionStorage
 * (per-tab) and the URL hash (shareable).
 *
 * Read order on mount: URL hash takes precedence over sessionStorage. If
 * neither is present we leave initial state alone.
 *
 * Writes happen on every state change after the catalog has loaded.
 */
export function useStatePersistence({
  tenantSlug,
  apiKey,
  state,
  dispatch,
  selectRootItem,
  resolveModelByCode,
  resolveItemIdsByCodes,
}: UseStatePersistenceOptions): void {
  const storageKey = buildStorageKey(tenantSlug, apiKey);
  const hydrationFiredRef = useRef(false);
  // Tracks whether step 1 actually found and dispatched persisted data.
  // Step 2 must only run when step 1 found persisted state to restore —
  // not when the user clicks a model card mid-session (which also causes
  // items to load). Without this guard, step 2 would fire on every model-
  // card click, read the stale URL hash (v=selection written during
  // SELECT_ROOT_ITEM), and dispatch HYDRATE clobbering ROOT_ITEM_LOADED's
  // view="configure" transition (widget stuck on selection grid, Bug 2).
  const hydrationHasPersistedDataRef = useRef(false);
  // Snapshot of persisted data captured at the moment step 1 first ran.
  // Step 2 reads from this snapshot instead of re-reading window.location.hash
  // at execution time, because by the time items load the persist effect may
  // have already overwritten the URL hash with a stale v=selection value
  // (written during SELECT_ROOT_ITEM before ROOT_ITEM_LOADED transitions the
  // view to "configure"). Re-reading the live hash in step 2 would produce
  // HYDRATE(view="selection"), clobbering the "configure" transition.
  const persistedSnapshotRef = useRef<{ itemCodes: string[]; view: import("../state").ViewType; activeFilters: Record<string, string> } | null>(null);

  // ── Hydration: run once, after the root catalog has loaded ──
  useEffect(() => {
    if (hydrationFiredRef.current) return;
    if (state.rootItems.length === 0) return; // wait for catalog
    hydrationFiredRef.current = true;

    const fromUrl = parseUrlHash(window.location.hash);
    const fromSession = parsePersistedState(sessionStorage.getItem(storageKey));

    // URL hash > sessionStorage > nothing
    const persisted: PersistedState | null = fromUrl
      ? {
          modelCode: fromUrl.modelCode,
          itemCodes: fromUrl.itemCodes,
          view: fromUrl.view,
          activeFilters: fromSession?.activeFilters ?? {},
        }
      : fromSession;

    if (!persisted || !persisted.modelCode) return;

    // Validate the model code resolves; if not, fall back to fresh start.
    const model = resolveModelByCode?.(persisted.modelCode) ?? null;
    if (!model) return;

    // There is genuine persisted state to restore — mark it so step 2 runs.
    hydrationHasPersistedDataRef.current = true;
    // Snapshot the restoration target now, before the persist effect can
    // overwrite the URL hash. Step 2 will read from this snapshot.
    persistedSnapshotRef.current = {
      itemCodes: persisted.itemCodes,
      // When the persisted view is "selection" but we know a model is being
      // restored (SELECT_ROOT_ITEM is about to fire), treat the intended
      // destination as "configure" — ROOT_ITEM_LOADED will set view=configure
      // and we must not HYDRATE back to "selection" over the top of it.
      view: persisted.view === "selection" ? "configure" : persisted.view,
      activeFilters: persisted.activeFilters ?? {},
    };

    // Restore the model through selectRootItem (NOT a bare SELECT_ROOT_ITEM
    // dispatch): it dispatches SELECT_ROOT_ITEM *and* fetches the model's
    // compatible items, ending in ROOT_ITEM_LOADED. A bare dispatch would flip
    // isLoading on without ever fetching, stranding the widget on the loading
    // skeleton after a refresh.
    void selectRootItem(model);

    // We can't HYDRATE selectedItems here because items haven't loaded yet.
    // selectRootItem will dispatch ROOT_ITEM_LOADED once the fetch resolves,
    // then we apply HYDRATE in step 2 below.
  }, [state.rootItems.length, storageKey, selectRootItem, resolveModelByCode]);

  // ── Hydration step 2: after items load, apply selectedItems + view ──
  const hydrationStep2FiredRef = useRef(false);
  useEffect(() => {
    if (hydrationStep2FiredRef.current) return;
    // Only proceed if step 1 found genuine persisted data to restore. This
    // prevents step 2 from firing when items load as a result of a fresh
    // model-card click (no persisted state → no HYDRATE to apply).
    if (!hydrationHasPersistedDataRef.current) return;
    if (state.items.length === 0) return;

    // Use the snapshot captured at step-1 time rather than re-reading
    // window.location.hash. By now the persist effect may have overwritten
    // the hash with v=selection (written when SELECT_ROOT_ITEM fired before
    // ROOT_ITEM_LOADED transitioned the view to "configure").
    const persisted = persistedSnapshotRef.current;
    if (!persisted) return;

    hydrationStep2FiredRef.current = true;

    const validIds = resolveItemIdsByCodes?.(persisted.itemCodes) ?? [];
    dispatch({
      type: "HYDRATE",
      payload: {
        selectedItemIds: validIds,
        view: persisted.view,
        activeFilters: persisted.activeFilters,
      },
    });
  }, [state.items.length, dispatch, resolveItemIdsByCodes]);

  // ── Persist on state change ──
  useEffect(() => {
    // Don't persist before hydration has had a chance to run, otherwise
    // we overwrite the persisted state with the empty initial state on a
    // fresh tab.
    if (state.rootItems.length === 0) return;

    const modelCode = state.selectedRootItem?.code ?? null;
    const itemCodes = state.items
      .filter((i) => state.selectedItems.has(i.id))
      .map((i) => i.code);

    const toPersist: PersistedState = {
      modelCode,
      itemCodes,
      view: state.view,
      activeFilters: state.activeFilters,
    };

    try {
      sessionStorage.setItem(storageKey, serializePersistedState(toPersist));
    } catch {
      // Ignore storage failures (private mode, full disk, etc.).
    }

    // Sync URL hash. Only encode model + items + view — never form fields.
    if (modelCode) {
      const newHash = serializeUrlHash({ modelCode, itemCodes, view: state.view });
      if (window.location.hash !== newHash) {
        // history.replaceState avoids polluting the back button.
        history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newHash}`);
      }
    } else if (window.location.hash) {
      // Selection cleared — drop the hash.
      history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }
  }, [
    storageKey,
    state.selectedRootItem,
    state.selectedItems,
    state.items,
    state.view,
    state.activeFilters,
    state.rootItems.length,
  ]);
}

/** Imperative helper for "Start over" CTAs — clears both sessionStorage and URL hash. */
export function clearPersistedState(tenantSlug: string, apiKey: string): void {
  try {
    sessionStorage.removeItem(buildStorageKey(tenantSlug, apiKey));
  } catch {
    // Ignore storage failures.
  }
  if (window.location.hash) {
    history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  }
}
